import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Observable, Subject, of, takeUntil, map, catchError } from 'rxjs';

import { EntitySelectComponent, EntitySelectPage } from '../../../../shared/components/entity-select/entity-select.component';
import { ContactoApiService } from '../../../contactos/services/contacto-api.service';
import { EmpresaApiService } from '../../../empresas/services/empresa-api.service';
import { DealApiService } from '../../../deals/services/deal-api.service';
import { QuoteApiService } from '../../../quotes/services/quote-api.service';
import { Contact } from '../../../contactos/models/contacto.model';
import { CompanyResponse } from '../../../empresas/models/empresa.model';
import { DealResponse } from '../../../deals/models/deal.model';
import { QuoteResponse } from '../../../quotes/models/quote.model';
import { ToastService } from '../../../../shared/services/toast.service';
import { WorkflowEnrollmentApiService } from '../services/workflow-enrollment-api.service';
import { WorkflowTimelineModalComponent } from '../workflow-timeline-modal/workflow-timeline-modal.component';
import { WorkflowExecutionApiService } from '../services/workflow-execution-api.service';
import { WorkflowWaitApiService } from '../services/workflow-wait-api.service';
import { WorkflowActionApiService } from '../services/workflow-action-api.service';
import { WorkflowActionExecuteResponse } from '../models/workflow-action.model';
import { WorkflowApiService } from '../services/workflow-api.service';
import { AuthService } from '../../../auth-pages/services/auth.service';
import {
  WorkflowEnrollmentResponse,
  WorkflowEnrollmentStatus,
  WorkflowManualEnrollmentRequest
} from '../models/workflow-runtime.model';
import {
  WorkflowExecutionContinueResponse,
  WorkflowConditionEvaluationResponse
} from '../models/workflow-execution.model';
import { WorkflowWaitResumeResponse } from '../models/workflow-wait.model';
import { WorkflowResponse, WORKFLOW_OBJECT_TYPES_ENABLED, WORKFLOW_OBJECT_TYPE_LABELS } from '../models/workflow.model';
import {
  workflowEnrolledByTypeLabel,
  workflowEnrollmentSourceLabel,
  workflowExitReasonLabel,
  workflowLifecycleStatusLabel,
  workflowNodeTypeLabel,
  workflowObjectTypeLabel,
  workflowOperatorLabel,
  workflowRecordLabel,
  workflowStatusLabel,
  workflowStepLabel
} from '../models/workflow-labels';
import { WorkflowRecipientListComponent } from '../shared/workflow-recipient-list.component';
import { WorkflowNodeLabelService } from '../shared/workflow-node-label.service';
import { branchKeyLabel } from '../workflow-builder/models/node-catalog';

@Component({
  selector: 'app-workflow-enrollments',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, FormsModule, WorkflowTimelineModalComponent, EntitySelectComponent, WorkflowRecipientListComponent],
  templateUrl: './workflow-enrollments.component.html'
})
export class WorkflowEnrollmentsComponent implements OnInit, OnDestroy {
  workflowId!: number;
  workflow: WorkflowResponse | null = null;
  enrollments: WorkflowEnrollmentResponse[] = [];

  loading = false;
  error = '';

  // Paginación
  currentPage = 0;
  pageSize = 20;
  totalElements = 0;
  totalPages = 0;

  // Filtros
  filterStatus: WorkflowEnrollmentStatus | '' = '';
  filterObjectType = '';
  filterObjectId: number | '' = '';
  hideTestRuns = true;

  // Modal inscripción manual
  showEnrollModal = false;
  enrollForm!: FormGroup;
  enrollLoading = false;
  enrollError = '';

  // Ejecutar acción actual (RF-06)
  executingActionId: number | null = null;
  actionResult: WorkflowActionExecuteResponse | null = null;
  showActionResultModal = false;

  // Continuar ejecución
  continuingId: number | null = null;
  continueResult: WorkflowExecutionContinueResponse | null = null;
  showContinueResultModal = false;

  // Reanudar espera (WAITING)
  showResumeModal = false;
  resumingEnrollment: WorkflowEnrollmentResponse | null = null;
  resumeBranchKey = 'DEFAULT';
  resumeLoading = false;
  resumeError = '';
  resumeResult: WorkflowWaitResumeResponse | null = null;
  readonly resumeBranchOptions = ['DEFAULT', 'TIMEOUT', 'EXIT'];

  // Cancelar enrollment
  showCancelModal = false;
  selectedEnrollmentToCancel: WorkflowEnrollmentResponse | null = null;
  cancelReason = '';
  isCancelling = false;
  cancelError = '';

  // Detalle de enrollment
  showDetailModal = false;
  detailEnrollment: WorkflowEnrollmentResponse | null = null;

  // Timeline RF-14
  timelineEnrollmentId: number | null = null;

  // Evaluar condición
  showEvaluateModal = false;
  evaluateEnrollmentId: number | null = null;
  evaluateNodeId: number | null = null;
  evaluateContextJson = '{}';
  evaluateLoading = false;
  evaluateResult: WorkflowConditionEvaluationResponse | null = null;
  evaluateError = '';

  readonly statusOptions: { value: WorkflowEnrollmentStatus | ''; label: string }[] = [
    { value: '', label: 'Todos los estados' },
    { value: 'ACTIVE',    label: 'Activo' },
    { value: 'WAITING',   label: 'Esperando' },
    { value: 'COMPLETED', label: 'Completado' },
    { value: 'EXITED',    label: 'Salido' },
    { value: 'FAILED',    label: 'Fallido' },
    { value: 'PAUSED',    label: 'Pausado' },
    { value: 'CANCELLED', label: 'Cancelado' }
  ];

  readonly objectTypeOptions = WORKFLOW_OBJECT_TYPES_ENABLED.map(t => ({
    value: t,
    label: WORKFLOW_OBJECT_TYPE_LABELS[t]
  }));

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private enrollmentApi: WorkflowEnrollmentApiService,
    private executionApi: WorkflowExecutionApiService,
    private waitApi: WorkflowWaitApiService,
    private actionApi: WorkflowActionApiService,
    private workflowApi: WorkflowApiService,
    private authService: AuthService,
    private nodeLabels: WorkflowNodeLabelService,
    private contactoApi: ContactoApiService,
    private empresaApi: EmpresaApiService,
    private dealApi: DealApiService,
    private quoteApi: QuoteApiService,
    private toast: ToastService,
    private fb: FormBuilder
  ) {}

  // ─── Selector de registro por nombre (inc 8) ─────────────────────────────
  recordDisplayText = '';

  /** Tipos de objeto con buscador por nombre disponible. */
  isSearchableType(): boolean {
    const t = this.enrollForm?.get('objectType')?.value;
    return t === 'CONTACT' || t === 'COMPANY' || t === 'DEAL' || t === 'QUOTE';
  }

  private get bId(): number {
    return this.authService.getCurrentUser()?.businessId || this.authService.getBusinessId() || 1;
  }

  recordSearch = (term: string, page: number): Observable<EntitySelectPage<any>> => {
    const businessId = this.bId;
    const type = this.enrollForm?.get('objectType')?.value;
    switch (type) {
      case 'CONTACT':
        return this.contactoApi.searchContacts({ businessId, searchTerm: term, page, size: 10 })
          .pipe(map((r) => ({ items: r.content, totalPages: r.totalPages })), catchError(() => of({ items: [], totalPages: 0 })));
      case 'COMPANY':
        return this.empresaApi.searchCompanies({ businessId, searchTerm: term, page, size: 10 })
          .pipe(map((r) => ({ items: r.content, totalPages: r.totalPages })), catchError(() => of({ items: [], totalPages: 0 })));
      case 'DEAL':
        return this.dealApi.searchDeals({ businessId, searchName: term, page, size: 10 })
          .pipe(map((r) => ({ items: r.content, totalPages: r.totalPages })), catchError(() => of({ items: [], totalPages: 0 })));
      case 'QUOTE':
        return this.quoteApi.searchQuotes({ businessId, searchQuoteNumber: term, page, size: 10 })
          .pipe(map((r) => ({ items: r.content, totalPages: r.totalPages })), catchError(() => of({ items: [], totalPages: 0 })));
      default:
        return of({ items: [], totalPages: 0 });
    }
  };

  recordLabel = (item: any): string => {
    const type = this.enrollForm?.get('objectType')?.value;
    switch (type) {
      case 'CONTACT': { const c = item as Contact; return `${c.fullname}${c.lastname ? ' ' + c.lastname : ''}`.trim(); }
      case 'COMPANY': { const c = item as CompanyResponse; return c.commercialName || c.businessName || `Empresa #${c.id}`; }
      case 'DEAL':    return (item as DealResponse).name;
      case 'QUOTE':   return (item as QuoteResponse).quoteNumber;
      default:        return String(item?.id ?? '');
    }
  };

  recordSublabel = (item: any): string => {
    const type = this.enrollForm?.get('objectType')?.value;
    switch (type) {
      case 'CONTACT': return (item as Contact).mail ?? '';
      case 'COMPANY': return (item as CompanyResponse).businessName ?? '';
      default:        return '';
    }
  };

  onRecordSelected(item: any | null): void {
    this.recordDisplayText = item ? this.recordLabel(item) : '';
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    this.workflowId = Number(id);
    this.initEnrollForm();
    this.loadWorkflow();
    this.loadEnrollments();
    // Nombres de los nodos: sin esto la columna «Nodo actual» pinta el id de `workflow_node`.
    this.nodeLabels.load(this.workflowId).pipe(takeUntil(this.destroy$)).subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initEnrollForm(): void {
    this.enrollForm = this.fb.group({
      workflowVersionId: [1, [Validators.required, Validators.min(1)]],
      objectType:        ['CONTACT', Validators.required],
      objectId:          [null, [Validators.required, Validators.min(1)]],
      reason:            ['']
    });

    // Al cambiar el tipo de objeto, limpiar el registro seleccionado (inc 8).
    this.enrollForm.get('objectType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.enrollForm.patchValue({ objectId: null }, { emitEvent: false });
        this.recordDisplayText = '';
      });
  }

  private loadWorkflow(): void {
    this.workflowApi.getById(this.workflowId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (wf) => {
        this.workflow = wf;
        // El tipo de objeto por defecto es el del workflow.
        if (wf.objectType) {
          this.enrollForm.patchValue({ objectType: wf.objectType }, { emitEvent: false });
        }
        if (wf.publishedVersionId) {
          this.enrollForm.patchValue({ workflowVersionId: wf.publishedVersionId });
        }
      },
      error: () => {}
    });
  }

  /**
   * Carga las inscripciones pidiendo al servidor los filtros y la página.
   *
   * Antes se llamaba al endpoint sin parámetros y se filtraba/contaba en memoria: se traían todas
   * las inscripciones del workflow y el paginador mostraba el total de lo descargado, no el real.
   */
  loadEnrollments(): void {
    this.loading = true;
    this.error = '';
    this.enrollmentApi.getByWorkflow(this.workflowId, {
      status:     this.filterStatus || undefined,
      objectType: this.filterObjectType || undefined,
      objectId:   this.filterObjectId !== '' ? Number(this.filterObjectId) : undefined,
      page:       this.currentPage,
      size:       this.pageSize
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (paged) => {
        this.enrollments = paged.content ?? [];
        this.totalElements = paged.totalElements ?? this.enrollments.length;
        this.totalPages = paged.totalPages ?? 1;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar las inscripciones.';
        this.loading = false;
      }
    });
  }

  onFilterChange(): void {
    this.currentPage = 0;
    this.loadEnrollments();
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.loadEnrollments();
  }

  get pages(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i);
  }

  /**
   * Único filtro que sigue siendo de cliente: el backend no expone un parámetro para excluir las
   * corridas de prueba, así que se descartan sobre la página ya recibida. Implica que una página
   * puede mostrar menos filas que `pageSize` cuando hay inscripciones de prueba intercaladas.
   */
  get filteredEnrollments(): WorkflowEnrollmentResponse[] {
    if (!this.hideTestRuns) return this.enrollments;
    return this.enrollments.filter(e => e.enrollmentSource !== 'TEST_RUN');
  }

  isTestRunEnrollment(e: WorkflowEnrollmentResponse): boolean {
    return e.enrollmentSource === 'TEST_RUN';
  }

  // ── Inscripción manual ────────────────────────────────────────────────

  openEnrollModal(): void {
    this.enrollForm.reset({
      workflowVersionId: this.workflow?.publishedVersionId ?? 1,
      objectType: this.workflow?.objectType ?? 'CONTACT',
      objectId: null,
      reason: ''
    });
    this.recordDisplayText = '';
    this.enrollError = '';
    this.showEnrollModal = true;
  }

  confirmEnroll(): void {
    if (this.enrollForm.invalid) { this.enrollForm.markAllAsTouched(); return; }
    const v = this.enrollForm.value;

    if (!this.workflow?.publishedVersionId) {
      this.enrollError = 'Este workflow no tiene una versión publicada. Publícalo desde el constructor antes de inscribir registros manualmente.';
      return;
    }

    this.enrollLoading = true;
    this.enrollError = '';

    const request: WorkflowManualEnrollmentRequest = {
      workflowVersionId: v.workflowVersionId,
      objectType: v.objectType,
      objectId: v.objectId,
      context: v.reason ? { reason: v.reason } : undefined
    };

    this.enrollmentApi.manualEnroll(this.workflowId, request).pipe(takeUntil(this.destroy$)).subscribe({
      next: (enrollment) => {
        this.enrollments = [enrollment, ...this.enrollments];
        this.closeEnrollModal();
        this.toast.created('Inscripción', `Se inscribió el registro con el número #${enrollment.id}.`);
      },
      error: (err) => {
        if (err.status === 403) {
          this.enrollError = 'No tienes permisos para realizar esta acción.';
        } else {
          this.enrollError = this.getWorkflowEnrollmentErrorMessage(err);
        }
        this.enrollLoading = false;
      }
    });
  }

  closeEnrollModal(): void {
    this.showEnrollModal = false;
    this.enrollLoading = false;
    this.enrollError = '';
  }

  // ── Continuar ejecución ───────────────────────────────────────────────

  continueEnrollment(enrollment: WorkflowEnrollmentResponse): void {
    this.continuingId = enrollment.id;
    this.executionApi.continueByEnrollment(enrollment.id, {}).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.continueResult = res;
        this.showContinueResultModal = true;
        this.continuingId = null;
        this.loadEnrollments();
      },
      error: (err) => {
        this.error = err.status === 403
          ? 'No tienes permisos para realizar esta acción.'
          : (err.error?.message || 'No se pudo continuar la ejecución.');
        this.continuingId = null;
      }
    });
  }

  closeContinueModal(): void {
    this.showContinueResultModal = false;
    this.continueResult = null;
  }

  getContinueMessage(res: WorkflowExecutionContinueResponse): string {
    if (res.status === 'COMPLETED') return 'El workflow llegó al nodo final y la inscripción fue completada.';
    if (res.status === 'WAITING' || res.nextNodeType === 'WAIT') return 'La inscripción quedó en espera.';
    if (res.nextNodeType === 'END') return 'El workflow llegó al nodo final.';
    if (res.nextNodeType === 'ACTION') {
      const at = res.details?.['actionType'];
      return at ? `Acción ejecutada: ${at}.` : 'El workflow avanzó a una acción.';
    }
    return res.message ?? 'Ejecución avanzada.';
  }

  isActionResult(res: WorkflowExecutionContinueResponse): boolean {
    return !!(res.details?.['actionType'] || res.details?.['actionStatus']);
  }

  // ── Ejecutar acción RF-06 ─────────────────────────────────────────────

  executeAction(enrollment: WorkflowEnrollmentResponse): void {
    this.executingActionId = enrollment.id;
    this.actionApi.executeCurrent(enrollment.id, {}).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.actionResult = res;
        this.showActionResultModal = true;
        this.executingActionId = null;
        this.loadEnrollments();
      },
      error: (err) => {
        const msg = err.error?.message ?? '';
        if (err.status === 403) this.error = 'No tienes permisos para realizar esta acción.';
        else if (msg.toLowerCase().includes('ticket')) this.error = 'No se pudo procesar la solicitud sobre el ticket.';
        else if (msg.toLowerCase().includes('delete') || msg.toLowerCase().includes('dangerous')) this.error = msg || 'Acción peligrosa no permitida. Verifica permisos y settings del workflow.';
        else if (msg.toLowerCase().includes('sms') || msg.toLowerCase().includes('slack') || msg.toLowerCase().includes('whatsapp')) this.error = 'La integración no está configurada.';
        else if (msg.toLowerCase().includes('asociar') || msg.toLowerCase().includes('associate')) this.error = 'No se pudo asociar los registros con la relación solicitada.';
        else if (msg.toLowerCase().includes('crear') || msg.toLowerCase().includes('create')) this.error = 'No se pudo crear el registro porque faltan campos requeridos.';
        else this.error = msg || 'No se pudo ejecutar la acción.';
        this.executingActionId = null;
      }
    });
  }

  /** Destinatarios detallados que devuelven las acciones de email dentro de `output`. */
  get actionResultRecipients(): unknown {
    return this.actionResult?.output?.['recipients'] ?? null;
  }

  closeActionResultModal(): void {
    this.showActionResultModal = false;
    this.actionResult = null;
  }

  getActionStatusClass(status: string | undefined): string {
    switch (status?.toUpperCase()) {
      case 'SUCCESS': return 'bg-green-100 text-green-700';
      case 'FAILED':  return 'bg-red-100 text-red-700';
      case 'SKIPPED': return 'bg-gray-100 text-gray-500';
      default:        return 'bg-blue-100 text-blue-700';
    }
  }

  // ── Reanudar espera ───────────────────────────────────────────────────

  openResumeModal(enrollment: WorkflowEnrollmentResponse): void {
    this.resumingEnrollment = enrollment;
    this.resumeBranchKey = 'DEFAULT';
    this.resumeError = '';
    this.resumeResult = null;
    this.showResumeModal = true;
  }

  confirmResume(): void {
    if (!this.resumingEnrollment) return;
    this.resumeLoading = true;
    this.resumeError = '';
    this.waitApi.resume(this.resumingEnrollment.id, { branchKey: this.resumeBranchKey })
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: (res) => {
          this.resumeResult = res;
          this.resumeLoading = false;
          this.loadEnrollments();
        },
        error: (err) => {
          const msg = err.error?.message ?? '';
          if (err.status === 403) this.resumeError = 'No tienes permisos para realizar esta acción.';
          else if (msg.toLowerCase().includes('rama') || msg.toLowerCase().includes('branch')) this.resumeError = 'No existe una rama configurada para reanudar la espera.';
          else if (msg.toLowerCase().includes('activ') || msg.toLowerCase().includes('active')) this.resumeError = 'No se encontró una espera activa para esta inscripción.';
          else this.resumeError = msg || 'No se pudo reanudar la espera.';
          this.resumeLoading = false;
        }
      });
  }

  closeResumeModal(): void {
    this.showResumeModal = false;
    this.resumingEnrollment = null;
    this.resumeResult = null;
    this.resumeLoading = false;
    this.resumeError = '';
  }

  // ── Evaluar condición ─────────────────────────────────────────────────

  openEvaluateModal(enrollment: WorkflowEnrollmentResponse): void {
    this.evaluateEnrollmentId = enrollment.id;
    this.evaluateNodeId = enrollment.currentNodeId ?? null;
    this.evaluateContextJson = '{}';
    this.evaluateResult = null;
    this.evaluateError = '';
    this.showEvaluateModal = true;
  }

  confirmEvaluate(): void {
    if (!this.evaluateEnrollmentId || !this.evaluateNodeId) {
      this.evaluateError = 'Debes ingresar un ID de nodo de condición.';
      return;
    }
    let context: Record<string, any> = {};
    try { context = JSON.parse(this.evaluateContextJson || '{}'); } catch {
      this.evaluateError = 'Contexto JSON inválido.'; return;
    }
    this.evaluateLoading = true;
    this.evaluateError = '';
    this.executionApi.evaluateCondition(this.evaluateEnrollmentId, this.evaluateNodeId, { context })
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: (res) => { this.evaluateResult = res; this.evaluateLoading = false; },
        error: (err) => {
          this.evaluateError = err.error?.message || 'No se pudo evaluar la condición.';
          this.evaluateLoading = false;
        }
      });
  }

  closeEvaluateModal(): void {
    this.showEvaluateModal = false;
    this.evaluateEnrollmentId = null;
    this.evaluateNodeId = null;
    this.evaluateResult = null;
    this.evaluateLoading = false;
    this.evaluateError = '';
  }

  // ── Cancelar enrollment ───────────────────────────────────────────────

  isCancelableStatus(status: WorkflowEnrollmentStatus): boolean {
    return status === 'ACTIVE' || status === 'WAITING' || status === 'PAUSED';
  }

  openCancelModal(enrollment: WorkflowEnrollmentResponse): void {
    this.selectedEnrollmentToCancel = enrollment;
    this.cancelReason = '';
    this.cancelError = '';
    this.showCancelModal = true;
  }

  confirmCancelEnrollment(): void {
    if (!this.selectedEnrollmentToCancel) return;
    this.isCancelling = true;
    this.cancelError = '';
    const reason = this.cancelReason.trim() || 'Cancelado manualmente desde la interfaz';

    this.enrollmentApi.cancelEnrollment(this.selectedEnrollmentToCancel.id, reason)
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: (updated) => {
          this.enrollments = this.enrollments.map(e =>
            e.id === updated.id ? updated : e
          );
          this.closeCancelModal();
          this.toast.success({ title: 'Inscripción cancelada', message: 'El registro dejó de avanzar por el flujo.' });
        },
        error: (err) => {
          if (err.status === 403) {
            this.cancelError = 'No tienes permisos para realizar esta acción.';
          } else {
            this.cancelError = this.getWorkflowEnrollmentErrorMessage(err);
          }
          this.isCancelling = false;
        }
      });
  }

  closeCancelModal(): void {
    this.showCancelModal = false;
    this.selectedEnrollmentToCancel = null;
    this.cancelReason = '';
    this.cancelError = '';
    this.isCancelling = false;
  }

  // ── Detalle de enrollment ─────────────────────────────────────────────────

  openDetailModal(enrollment: WorkflowEnrollmentResponse): void {
    this.detailEnrollment = enrollment;
    this.showDetailModal = true;
  }

  closeDetailModal(): void {
    this.showDetailModal = false;
    this.detailEnrollment = null;
  }

  openCancelFromDetail(): void {
    if (!this.detailEnrollment) return;
    this.closeDetailModal();
    this.openCancelModal(this.detailEnrollment);
  }

  getEnrollmentContextKeys(context: Record<string, any> | undefined): string[] {
    return context ? Object.keys(context) : [];
  }

  // ── Helper de errores RF-12 ───────────────────────────────────────────

  getWorkflowEnrollmentErrorMessage(err: any): string {
    const errBody = err?.error ?? {};
    const code: string =
      errBody?.code ??
      errBody?.errorCode ??
      errBody?.details?.code ??
      errBody?.details ??
      err?.code ??
      '';

    switch (code) {
      case 'WORKFLOW_ALREADY_ENROLLED_ONCE':
        return 'Este registro ya fue inscrito previamente en este workflow y la re-inscripción está deshabilitada.';
      case 'WORKFLOW_DUPLICATE_ACTIVE_ENROLLMENT':
        return 'Este registro ya tiene una inscripción activa en este workflow.';
      case 'WORKFLOW_ACTIVE_ENROLLMENT_LIMIT_REACHED':
        return 'Este registro alcanzó el límite de inscripciones activas permitidas para este workflow.';
      case 'WORKFLOW_ENROLLMENT_NOT_CANCELABLE':
        return 'Esta inscripción ya no puede cancelarse porque no está en un estado activo.';
      case 'WORKFLOW_OBJECT_SUPPRESSED':
        return 'Este registro está excluido de este workflow por una regla de supresión.';
      case 'WORKFLOW_EXIT_CRITERIA_MATCHED':
        return 'El registro cumple un criterio global de salida y fue retirado del workflow.';
      case 'WORKFLOW_REENROLLMENT_START_NODE_NOT_FOUND':
        return 'El nodo configurado para re-inscripción no existe en este workflow.';
      case 'WORKFLOW_INVALID_EXIT_CRITERIA':
        return 'Hay un criterio de salida inválido. Revisa la configuración del workflow.';
      case 'WORKFLOW_INVALID_SUPPRESSION_RULE':
        return 'Hay una regla de supresión inválida. Revisa la configuración del workflow.';
      case 'WORKFLOW_INVALID_REENROLLMENT_START_MODE':
        return 'El modo de inicio de re-inscripción no es válido.';
    }

    const msg: string = errBody?.message ?? err?.message ?? '';
    // El backend responde con el nombre interno del trigger ("MANUAL_ENROLLMENT"), que no dice
    // nada al usuario: la inscripción a mano solo existe si el flujo arranca con el disparador
    // «Inscripción manual». Con cualquier otro (evento del CRM, webhook…) hay que usar el
    // probador de disparadores.
    if (msg.toUpperCase().includes('MANUAL_ENROLLMENT')) {
      return 'Este workflow no se puede inscribir a mano porque su disparador no es «Inscripción manual». '
        + 'Para probarlo, usa «Probar disparador» y envía el evento que lo activa.';
    }
    if (msg.toLowerCase().includes('activ')) {
      return 'El workflow debe estar activo para inscribir registros.';
    }
    if (msg.toLowerCase().includes('inscrit') || msg.toLowerCase().includes('enrolled')) {
      return 'El registro ya se encuentra inscrito en este workflow.';
    }
    if (msg.toLowerCase().includes('supresi') || msg.toLowerCase().includes('suppression')) {
      return 'El registro está excluido por reglas de supresión.';
    }
    return msg || 'No se pudo completar la operación.';
  }

  // ── Visuals ───────────────────────────────────────────────────────────

  /**
   * Etiquetas legibles. TODAS delegan en el diccionario único del módulo
   * (`models/workflow-labels.ts`): esta pantalla pintaba `CONTACT`, `SEND_INTERNAL_NOTIFICATION`,
   * `EXIT_CRITERIA`, `SYSTEM` y `SUCCESS` en crudo porque el DTO de la LISTA de inscripciones
   * (`WorkflowEnrollmentResponse`) no trae los campos `*Label` que sí manda el de timeline.
   */
  readonly statusText = workflowStatusLabel;
  readonly objectTypeText = workflowObjectTypeLabel;
  readonly recordText = workflowRecordLabel;
  readonly exitReasonText = workflowExitReasonLabel;
  readonly enrolledByText = workflowEnrolledByTypeLabel;
  readonly enrollmentSourceText = workflowEnrollmentSourceLabel;
  readonly nodeTypeText = workflowNodeTypeLabel;
  readonly stepText = workflowStepLabel;
  readonly operatorText = workflowOperatorLabel;
  readonly workflowStatusText = workflowLifecycleStatusLabel;
  /** Nombre de la rama: DEFAULT/TIMEOUT/EXIT tienen texto; las que nombra el usuario van tal cual. */
  branchLabel(key: string | null | undefined): string {
    return key ? branchKeyLabel(key) : '—';
  }

  /** Nombre del nodo (título o subtipo); «Nodo #id» mientras el grafo no llega. */
  nodeText(nodeId: number | null | undefined): string {
    return this.nodeLabels.labelFor(this.workflowId, nodeId);
  }

  getStatusLabel(status: WorkflowEnrollmentStatus): string {
    return workflowStatusLabel(status);
  }

  getStatusClass(status: WorkflowEnrollmentStatus): string {
    switch (status) {
      case 'ACTIVE':    return 'bg-green-100 text-green-700';
      case 'WAITING':   return 'bg-yellow-100 text-yellow-700';
      case 'COMPLETED': return 'bg-blue-100 text-blue-700';
      case 'EXITED':    return 'bg-gray-100 text-gray-500';
      case 'FAILED':    return 'bg-red-100 text-red-700';
      case 'PAUSED':    return 'bg-orange-100 text-orange-700';
      case 'CANCELLED': return 'bg-gray-200 text-gray-600';
      default:          return 'bg-gray-100 text-gray-500';
    }
  }

  isFieldInvalid(field: string): boolean {
    const ctrl = this.enrollForm.get(field);
    return !!(ctrl?.invalid && ctrl?.touched);
  }
}
